// src/components/templates/ClientFormDialog.jsx
import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ClientForm from "../organisms/ClientForm";
import { useClientForm } from "../../hooks/useClientForm";

/**
 * ClientFormDialog — wraps ClientForm in a Dialog for create/edit mode.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {function} props.onSuccess  — called after successful save
 * @param {object|null} props.client  — if provided, edit mode; otherwise create
 */
export default function ClientFormDialog({ open, onClose, onSuccess, client }) {
  const mode = client ? "edit" : "create";

  const { form, errors, serverError, loading, handleChange, handleBlur, handleSubmit, reset } =
    useClientForm({ mode, initialValues: client, onSuccess, onClose });

  // Reset form whenever dialog opens/client changes
  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          {mode === "create" ? <PersonAddRoundedIcon /> : <EditRoundedIcon />}
          <span>{mode === "create" ? "Add New Client" : "Edit Client"}</span>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <ClientForm
          form={form}
          errors={errors}
          serverError={serverError}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? "Saving…" : mode === "create" ? "Add Client" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
